import moment, { Moment } from "moment";

import * as api from "@nens/threedi/openapi-client";

import { RainBarChartData } from "../MainScreen/RainBarChart";
import { RainRaster, RasterAggregateResult } from "../types/rasterRain";

export const getRasterStartMoment = function (rainRaster: RainRaster) {
  if (rainRaster.rasterStart.kind === "RasterTimeAbsolute") {
    return moment(new Date(rainRaster.rasterStart.dateTime));
  } else {
    const momentDate = moment(new Date());
    momentDate.add({ hour: rainRaster.rasterStart.relativeHours });
    return momentDate;
  }
};
export const getRasterStopMoment = function (rainRaster: RainRaster) {
  if (rainRaster.rasterEnd.kind === "RasterTimeAbsolute") {
    return moment(new Date(rainRaster.rasterEnd.dateTime));
  } else {
    const momentDate = moment(new Date());
    momentDate.add({ hour: rainRaster.rasterEnd.relativeHours });
    return momentDate;
  }
};

// The amount of rain in mm needed to reach the top of the chart
// in mm per day https://github.com/nens/threedi-api/issues/1662#issuecomment-1215075109
// export const MaximumMmChartRainPerTimestep = {
//   "5min": 8, //mm
//   "10min": 16, //mm
//   "H": 100, //mm
//   "D": 200, //mm
// }
// roughly based on designbui 8
export const MaximumMmChartRainPerTimestep = {
  "5min": 3.5, //mm
  "10min": 5, //mm
  H: 10, //mm
  D: 20, //mm
};
// dummie data
// export const MaximumMmChartRainPerTimestep = {
//   "5min": 1, //mm
//   "10min": 1, //mm
//   "H": 1, //mm
//   "D": 1, //mm
// }
//export const amountMmThatDayIsConsideredToHavePlentyRain = 100;
// roughly based on designbui 8
// export const amountMmThatDayIsConsideredToHavePlentyRain = 20;
// just guessed
export const amountMmThatDayIsConsideredToHavePlentyRain = 2;
// https://pandas.pydata.org/pandas-docs/stable/user_guide/timeseries.html#dateoffset-objects
export const pandaDurationStringMiliseconds = {
  "5min": 60 * 5 * 1000,
  "10min": 60 * 10 * 1000,
  H: 60 * 60 * 1000,
  D: 60 * 60 * 24 * 1000,
};

export const dayHasRainInRasterAggregationresult = (
  day: Moment,
  rasterAggregateResult: RasterAggregateResult
) => {
  const thresholdRainValue = amountMmThatDayIsConsideredToHavePlentyRain;
  const dayRain =
    rasterAggregateResult &&
    rasterAggregateResult.results &&
    rasterAggregateResult.results.find(({ time, value }) => {
      const timeMoment: Moment = moment(new Date(time));
      const datePickerMoment = moment(new Date(day.valueOf()));
      return datePickerMoment.isSame(timeMoment, "day") && value > thresholdRainValue;
    });
  if (dayRain) {
    return true;
  } else {
    return false;
  }
};

// https://github.com/nens/threedi-api/issues/1662#issuecomment-1215075109
export const getMaxValueForBarchartInMm = (
  data: RainBarChartData,
  rainRaster: RainRaster
): number => {
  const dataPoint1Time = data[0]?.time;
  const dataPoint2Time = data[1]?.time;

  if (typeof dataPoint1Time === "number" && typeof dataPoint2Time === "number") {
    const diff = dataPoint1Time - dataPoint2Time;
    if (diff > 1000 * 60 * 4 && diff <= 1000 * 60 * 7) {
      return MaximumMmChartRainPerTimestep["5min"];
    } else if (diff > 1000 * 60 * 7 && diff <= 1000 * 60 * 12) {
      return MaximumMmChartRainPerTimestep["10min"];
    } else if (diff > 1000 * 60 * 12 && diff < 1000 * 60 * 80) {
      return MaximumMmChartRainPerTimestep["H"];
    } else {
      return MaximumMmChartRainPerTimestep["D"];
    }
  } else {
    if (rainRaster.frequency === "5min") {
      return MaximumMmChartRainPerTimestep["5min"];
    } else if (rainRaster.frequency === "10min") {
      return MaximumMmChartRainPerTimestep["10min"];
    } else if (rainRaster.frequency === "H") {
      return MaximumMmChartRainPerTimestep["H"];
    } else if (rainRaster.frequency === "D") {
      return MaximumMmChartRainPerTimestep["D"];
    } else {
      return MaximumMmChartRainPerTimestep["D"];
    }
  }
};

export const getRasterAggregateUrl = (
  modelPolygon: string,
  rasterUuid: string,
  startDateTime: string,
  stopDateTime: string,
  // as described in the api docs here:
  // https://demo.lizard.net/api/v4/rasters/d6c2347d-7bd1-4d9d-a1f6-b342c865516f/zonal/?zonal_statistic=mean&geom=POLYGON((+4.655663105256908+52.59085747199397,+4.808619107856246+52.59085747199397,+4.808619107856246+52.697177256067306,+4.655663105256908+52.697177256067306,+4.655663105256908+52.59085747199397))&start=2022-08-11T12:11:29Z&stop=2022-08-22T12:11:29Z
  // parameters for "frequency" can be found here:
  // https://pandas.pydata.org/pandas-docs/stable/user_guide/timeseries.html#dateoffset-objects
  frequency: string
) => {
  return (
    `https://demo.lizard.net/api/v4/rasters/${rasterUuid}/zonal/?` +
    `zonal_statistic=mean&` +
    `temporal_statistic=sum&` +
    `frequency=${frequency}&` +
    `geom=${modelPolygon}&` +
    `start=${startDateTime}&` +
    `stop=${stopDateTime}`
  );
};

export const mmPerHourToMmPerDuration = (value: number, durationInMiliseconds: number) => {
  const hourMiliseconds = 1000 * 60 * 60;
  return (value * durationInMiliseconds) / hourMiliseconds;
};

export const meterPerSecondToMmPerHour = (value: number) => {
  return value * 1000 * 60 * 60;
};

export const meterPerSecondToMmPerDuration = (value: number, durationInMiliseconds: number) => {
  return mmPerHourToMmPerDuration(meterPerSecondToMmPerHour(value), durationInMiliseconds);
};

export const pandaDurationStringToMiliseconds = (str: string) => {
  if (str === "5min" || str === "10min" || str === "H" || str === "D") {
    return pandaDurationStringMiliseconds[str];
  } else {
    // ughh
    return 9999;
  }
};

export const rasterAggregateResultToMmPerDuration = (
  rasterAggregateResult: RasterAggregateResult,
  frequency: string,
  unit: api.RasterRainUnitsEnum
) => {
  return {
    ...rasterAggregateResult,
    results: !rasterAggregateResult.results
      ? null
      : rasterAggregateResult.results.map(({ time, value }) => {
          const newValue =
            unit === api.RasterRainUnitsEnum.MM_H
              ? mmPerHourToMmPerDuration(value, pandaDurationStringToMiliseconds(frequency))
              : unit === api.RasterRainUnitsEnum.M_S
              ? meterPerSecondToMmPerDuration(value, pandaDurationStringToMiliseconds(frequency))
              : /*unit === api.RasterRainUnitsEnum.MM ? */ value;

          return {
            time: time,
            value: newValue,
          };
        }),
  };
};
